import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  ASSET_STATUS_LABELS,
  ASSET_STATUSES,
  ASSET_TYPE_LABELS,
  ASSET_TYPES,
  isExceptionStatus,
  roleCanEditCatalog,
  type AssetListResponse,
  type AssetResponse,
  type AssetStatus,
  type AssetType,
  type BinListResponse,
  type ContainerKind,
  type PublicAsset,
  type PublicBin,
  type PublicTote,
  type PublicTrailer,
  type ToteListResponse,
  type TrailerListResponse,
} from '@inventohub/shared';
import { ApiClientError, apiDownload, apiRequest, triggerDownload } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Draft {
  serial: string;
  type: AssetType;
  status: AssetStatus;
  notes: string;
  containerKind: ContainerKind;
  containerId: string;
}

const EMPTY: Draft = {
  serial: '',
  type: 'camera',
  status: 'available',
  notes: '',
  containerKind: 'uncontained',
  containerId: '',
};

export function AssetsPage(): JSX.Element {
  const { user } = useAuth();
  const canEdit = user ? roleCanEditCatalog(user.role) : false;
  const [assets, setAssets] = useState<PublicAsset[]>([]);
  const [totes, setTotes] = useState<PublicTote[]>([]);
  const [bins, setBins] = useState<PublicBin[]>([]);
  const [trailers, setTrailers] = useState<PublicTrailer[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [assetBody, toteBody, binBody, trailerBody] = await Promise.all([
          apiRequest<AssetListResponse>('/api/assets'),
          apiRequest<ToteListResponse>('/api/totes'),
          apiRequest<BinListResponse>('/api/bins'),
          apiRequest<TrailerListResponse>('/api/trailers'),
        ]);
        if (cancelled) return;
        setAssets(assetBody.assets);
        setTotes(toteBody.totes);
        setBins(binBody.bins);
        setTrailers(trailerBody.trailers);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load assets.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<AssetResponse>('/api/assets', {
        method: 'POST',
        body: {
          serial: draft.serial,
          type: draft.type,
          status: draft.status,
          notes: draft.notes || null,
          containerKind: draft.containerKind,
          ...(draft.containerKind === 'uncontained' ? {} : { containerId: draft.containerId }),
        },
      });
      setAssets((current) =>
        [...current.filter((entry) => entry.id !== body.asset.id), body.asset].sort((a, b) =>
          a.serial.localeCompare(b.serial),
        ),
      );
      setDraft(EMPTY);
      setNotice(`Added ${body.asset.serial}.`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the asset.');
    }
  }

  async function printSelected(): Promise<void> {
    const serials = assets.filter((asset) => selected[asset.id]).map((asset) => asset.serial);
    if (serials.length === 0) {
      setError('Select at least one asset to print.');
      return;
    }
    setError(null);
    try {
      const { blob, filename } = await apiDownload('/api/labels', {
        method: 'POST',
        body: { assetSerials: serials },
      });
      triggerDownload(blob, filename);
      setNotice(`Printed ${serials.length} label${serials.length === 1 ? '' : 's'}.`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not print labels.');
    }
  }

  const containerOptions =
    draft.containerKind === 'tote'
      ? totes.map((tote) => ({ id: tote.id, label: `Tote ${tote.number}` }))
      : draft.containerKind === 'bin'
        ? bins.map((bin) => ({ id: bin.id, label: bin.name }))
        : draft.containerKind === 'trailer'
          ? trailers.map((trailer) => ({ id: trailer.id, label: trailer.name }))
          : [];

  return (
    <section data-testid="assets-page">
      <div className="card">
        <h2>Assets</h2>
        <p className="meta">Serialized gear. Mats do not have to live in a tote.</p>
        {error ? (
          <p className="status status--error" data-testid="assets-error">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="status status--ok" data-testid="assets-notice">
            {notice}
          </p>
        ) : null}
      </div>

      {canEdit ? (
        <form className="card" onSubmit={(event) => void onCreate(event)}>
          <h2>Add asset</h2>
          <label className="field">
            Serial
            <input
              value={draft.serial}
              required
              autoComplete="off"
              onChange={(event) =>
                setDraft((current) => ({ ...current, serial: event.target.value }))
              }
              data-testid="asset-serial"
            />
          </label>
          <label className="field">
            Type
            <select
              value={draft.type}
              onChange={(event) =>
                setDraft((current) => ({ ...current, type: event.target.value as AssetType }))
              }
              data-testid="asset-type"
            >
              {ASSET_TYPES.map((type) => (
                <option key={type} value={type}>
                  {ASSET_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Status
            <select
              value={draft.status}
              onChange={(event) =>
                setDraft((current) => ({ ...current, status: event.target.value as AssetStatus }))
              }
            >
              {ASSET_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {ASSET_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Container
            <select
              value={draft.containerKind}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  containerKind: event.target.value as ContainerKind,
                  containerId: '',
                }))
              }
              data-testid="asset-container-kind"
            >
              <option value="uncontained">Uncontained (e.g. a mat)</option>
              <option value="tote">Tote</option>
              <option value="bin">Bin</option>
              <option value="trailer">Trailer</option>
            </select>
          </label>
          {draft.containerKind !== 'uncontained' ? (
            <label className="field">
              Place in
              <select
                value={draft.containerId}
                required
                onChange={(event) =>
                  setDraft((current) => ({ ...current, containerId: event.target.value }))
                }
              >
                <option value="">Select…</option>
                {containerOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="field">
            Notes
            <textarea
              value={draft.notes}
              onChange={(event) =>
                setDraft((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </label>
          <button type="submit" className="button" data-testid="asset-create">
            Create asset
          </button>
        </form>
      ) : null}

      {canEdit ? (
        <p>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => void printSelected()}
          >
            Print QR labels for selected
          </button>
        </p>
      ) : null}

      {loading ? <p className="meta">Loading assets…</p> : null}

      {assets.map((asset) => (
        <article className="card" key={asset.id} data-testid={`asset-row-${asset.serial}`}>
          {canEdit ? (
            <label className="check">
              <input
                type="checkbox"
                checked={Boolean(selected[asset.id])}
                onChange={(event) =>
                  setSelected((current) => ({ ...current, [asset.id]: event.target.checked }))
                }
              />
              Select
            </label>
          ) : null}
          <h2>
            <Link to={`/assets/${encodeURIComponent(asset.serial)}`}>{asset.serial}</Link>
          </h2>
          <p className="meta">
            {ASSET_TYPE_LABELS[asset.type]} · {ASSET_STATUS_LABELS[asset.status]}
            {asset.toteNumber
              ? ` · Tote ${asset.toteNumber}`
              : asset.containerName
                ? ` · ${asset.containerName}`
                : ''}
          </p>
          {isExceptionStatus(asset.status) ? (
            <p className="status status--warn" data-testid={`asset-exception-${asset.serial}`}>
              {ASSET_STATUS_LABELS[asset.status]}. Current loadout assignment is unchanged.
            </p>
          ) : null}
        </article>
      ))}
    </section>
  );
}
